/**
 * NovaVignette — "Nova exists" scenes from the Character Bible.
 *
 * Each vignette is a small narrative loop where Nova is doing
 * something instead of just standing in place. Used in long-loading
 * surfaces (Nova chat between messages, splash screen, etc.) so
 * she feels like she has a life of her own.
 *
 * Three variants:
 *   - 'workstation' — Nova at a tiny laptop, fingers tapping. The
 *     Orbit signature pulses softly on screen. (3s float · 0.8s tap
 *     cycle · 1.8s pulse · 6s antenna orbit.) Use for general loading.
 *   - 'garden'      — Nova waters a bud; stem grows, bloom opens.
 *     Then she gets curious. Then shocked. Then she draws a tiny
 *     blaster, zaps the flower, and smiles. 12s narrative loop.
 *     Use sparingly — long, dramatic, attention-grabbing.
 *   - 'family'      — One Nova photographs two more (Mars red,
 *     Andromeda blue). Shutter flashes every 3s. Use for the
 *     "Nova is thinking really hard" / Nova-chat first-time state.
 *
 * Implementation notes:
 *   - Pure SVG + CSS keyframes. Same theming-tokens-as-CSS-vars
 *     pattern as Nova.tsx, so a future theme swap follows.
 *   - All three vignettes share a 200×200 viewBox so the host
 *     container can size by height alone.
 */

import { cn } from '@/lib/utils';

export type NovaVignetteVariant = 'workstation' | 'garden' | 'family';

interface Props {
  variant?: NovaVignetteVariant;
  /** Pixel height; width auto from 1:1 viewBox. Default 220. */
  size?: number;
  className?: string;
  ariaLabel?: string;
}

// Palette pulled from the Character Bible; keep in sync with Nova.tsx.
const TEAL = 'hsl(var(--lunar-teal))';
const TEAL_DEEP = 'hsl(182 43% 22%)';
const IVORY = 'hsl(var(--cosmic-ivory))';
const NEAR_BLACK = 'hsl(36 10% 8%)';
const ORANGE = 'hsl(var(--orbit-orange))';
const PEACH = 'hsl(20 55% 72%)';
const MARS_RED = 'hsl(8 65% 50%)';
const ANDROMEDA = 'hsl(264 35% 50%)';
const SIGNAL_BLUE = 'hsl(207 60% 78%)';
const SOIL = 'hsl(28 24% 30%)';
const STEM = 'hsl(140 30% 35%)';
const BLOOM = 'hsl(340 60% 60%)';

export function NovaVignette({
  variant = 'workstation',
  size = 220,
  className,
  ariaLabel,
}: Props) {
  const label =
    ariaLabel ??
    (variant === 'workstation'
      ? 'Nova at her workstation'
      : variant === 'garden'
        ? 'Nova tending her garden'
        : 'Nova taking a family portrait');

  return (
    <div
      className={cn('relative inline-flex items-end justify-center', className)}
      style={{ width: size, height: size }}
      role="img"
      aria-label={label}
    >
      <svg viewBox="0 0 200 200" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        {variant === 'workstation' && <Workstation />}
        {variant === 'garden' && <Garden />}
        {variant === 'family' && <Family />}
      </svg>
      <style>{vignetteCSS}</style>
    </div>
  );
}

export default NovaVignette;

// ─── Workstation ────────────────────────────────────────────────

function Workstation() {
  return (
    <g className="nv-workstation">
      {/* Soft shadow under Nova */}
      <ellipse cx="100" cy="178" rx="48" ry="6" fill={PEACH} opacity={0.45} />

      {/* Body block — Nova sitting */}
      <g className="nv-w-body">
        {/* Antenna with orbiting dot */}
        <g className="nv-w-antenna">
          <line x1="100" y1="78" x2="100" y2="62" stroke={NEAR_BLACK} strokeWidth="1.6" strokeLinecap="round" />
          <ellipse cx="100" cy="58" rx="7.5" ry="2.2" stroke={NEAR_BLACK} strokeWidth="1" fill="none" />
          <circle className="nv-w-orbit-dot" cx="100" cy="58" r="2.4" fill={ORANGE} />
        </g>

        {/* Head — same device-frame structure as Nova.tsx */}
        <rect x="68" y="78" width="64" height="48" rx="11" fill={TEAL_DEEP} />
        <rect x="70" y="80" width="60" height="44" rx="9" fill={TEAL} />
        <rect x="76" y="86" width="48" height="28" rx="6" fill={NEAR_BLACK} />
        {/* Eyes */}
        <polygon points="84,96 92,96 88,109" fill={SIGNAL_BLUE} />
        <polygon points="108,96 116,96 112,109" fill={SIGNAL_BLUE} />
        {/* Cheeks — soft and content */}
        <circle cx="78" cy="112" r="2.2" fill={PEACH} opacity={0.85} />
        <circle cx="122" cy="112" r="2.2" fill={PEACH} opacity={0.85} />

        {/* Body block beneath — torso */}
        <rect x="62" y="124" width="76" height="36" rx="9" fill={TEAL_DEEP} />
        <rect x="64" y="126" width="72" height="32" rx="7" fill={TEAL} />

        {/* Arms reaching down to the laptop */}
        <rect x="62" y="138" width="6" height="20" rx="3" fill={TEAL_DEEP} className="nv-w-arm-left" />
        <rect x="132" y="138" width="6" height="20" rx="3" fill={TEAL_DEEP} className="nv-w-arm-right" />
      </g>

      {/* Tiny laptop in front */}
      <g className="nv-w-laptop">
        {/* Open lid (screen) */}
        <rect x="62" y="148" width="76" height="20" rx="3" fill={TEAL_DEEP} />
        <rect x="65" y="151" width="70" height="14" rx="2" fill={NEAR_BLACK} />
        {/* Orbit signature on screen — pulsing */}
        <text
          x="100"
          y="161"
          textAnchor="middle"
          fontFamily="ui-sans-serif, system-ui"
          fontSize="6"
          fontWeight="700"
          fill={ORANGE}
          className="nv-w-screen-pulse"
        >
          ORBIT
        </text>
        {/* Base */}
        <rect x="58" y="168" width="84" height="4" rx="2" fill={TEAL_DEEP} />
      </g>
    </g>
  );
}

// ─── Garden ─────────────────────────────────────────────────────

function Garden() {
  return (
    <g className="nv-garden">
      {/* Ground line */}
      <line x1="20" y1="170" x2="180" y2="170" stroke={NEAR_BLACK} strokeWidth="1.2" />

      {/* Pot + stem + bloom — grows on the loop */}
      <g className="nv-g-plant">
        <rect x="138" y="148" width="22" height="22" rx="2" fill={SOIL} />
        <line className="nv-g-stem" x1="149" y1="148" x2="149" y2="148" stroke={STEM} strokeWidth="2" strokeLinecap="round" />
        <circle className="nv-g-bloom" cx="149" cy="120" r="0" fill={BLOOM} />
      </g>

      {/* Zap beam — only visible during the zap phase */}
      <line
        className="nv-g-zap"
        x1="116"
        y1="125"
        x2="146"
        y2="120"
        stroke={ORANGE}
        strokeWidth="2"
        strokeLinecap="round"
        opacity={0}
      />

      {/* Nova body — face changes during the loop via opacity-swapped eye sets */}
      <g>
        {/* Antenna */}
        <line x1="60" y1="80" x2="60" y2="64" stroke={NEAR_BLACK} strokeWidth="1.6" strokeLinecap="round" />
        <ellipse cx="60" cy="60" rx="7.5" ry="2.2" stroke={NEAR_BLACK} strokeWidth="1" fill="none" />
        <circle cx="60" cy="60" r="2.4" fill={ORANGE} />

        {/* Head */}
        <rect x="28" y="80" width="64" height="48" rx="11" fill={TEAL_DEEP} />
        <rect x="30" y="82" width="60" height="44" rx="9" fill={TEAL} />
        <rect x="36" y="88" width="48" height="28" rx="6" fill={NEAR_BLACK} />

        {/* Eye stacks — only one set is visible per phase. Each has a
            class that an animation targets via opacity. Bible-correct
            signal-blue eye color throughout. */}
        <g className="nv-g-eyes nv-g-eyes-idle">
          <polygon points="44,98 54,98 49,111" fill={SIGNAL_BLUE} />
          <polygon points="66,98 76,98 71,111" fill={SIGNAL_BLUE} />
        </g>
        <g className="nv-g-eyes nv-g-eyes-curious" opacity={0}>
          <polygon
            points="44,98 54,98 49,111"
            fill={SIGNAL_BLUE}
            style={{ transformOrigin: '49px 105px', transform: 'rotate(-15deg)' }}
          />
          <polygon
            points="66,98 76,98 71,111"
            fill={SIGNAL_BLUE}
            style={{ transformOrigin: '71px 105px', transform: 'rotate(15deg)' }}
          />
        </g>
        <g className="nv-g-eyes nv-g-eyes-shocked" opacity={0}>
          <circle cx="49" cy="104" r="7" fill={SIGNAL_BLUE} />
          <circle cx="71" cy="104" r="7" fill={SIGNAL_BLUE} />
          <circle cx="49" cy="104" r="2.4" fill={NEAR_BLACK} />
          <circle cx="71" cy="104" r="2.4" fill={NEAR_BLACK} />
        </g>
        <g className="nv-g-eyes nv-g-eyes-smug" opacity={0}>
          <path d="M 43 106 Q 49 99 55 106" stroke={SIGNAL_BLUE} strokeWidth="2.4" fill="none" strokeLinecap="round" />
          <path d="M 65 106 Q 71 99 77 106" stroke={SIGNAL_BLUE} strokeWidth="2.4" fill="none" strokeLinecap="round" />
        </g>

        {/* Body */}
        <rect x="22" y="126" width="76" height="36" rx="9" fill={TEAL_DEEP} />
        <rect x="24" y="128" width="72" height="32" rx="7" fill={TEAL} />

        {/* Arm + watering can — slides in early, slides out before zap */}
        <g className="nv-g-watering">
          <rect x="92" y="138" width="22" height="4" rx="2" fill={TEAL_DEEP} />
          <rect x="112" y="132" width="14" height="14" rx="2" fill={IVORY} />
          {/* Water drops */}
          <line x1="128" y1="146" x2="132" y2="156" stroke={IVORY} strokeWidth="1.4" strokeLinecap="round" />
          <line x1="124" y1="148" x2="128" y2="158" stroke={IVORY} strokeWidth="1.4" strokeLinecap="round" />
        </g>

        {/* Arm + tiny blaster — slides in for the zap */}
        <g className="nv-g-blaster" opacity={0}>
          <rect x="92" y="138" width="22" height="4" rx="2" fill={TEAL_DEEP} />
          <rect x="112" y="134" width="10" height="10" rx="1" fill={NEAR_BLACK} />
          <rect x="120" y="137" width="6" height="3" rx="1" fill={ORANGE} />
        </g>
      </g>
    </g>
  );
}

// ─── Family portrait ────────────────────────────────────────────

function Family() {
  // One Nova on the left holds a camera; two siblings (Mars red,
  // Andromeda blue) on the right pose. Shutter flashes every 3s.
  return (
    <g className="nv-family">
      {/* Photographer Nova (teal) */}
      <g className="nv-f-photographer">
        <line x1="48" y1="68" x2="48" y2="52" stroke={NEAR_BLACK} strokeWidth="1.4" strokeLinecap="round" />
        <ellipse cx="48" cy="48" rx="6" ry="1.8" stroke={NEAR_BLACK} strokeWidth="1" fill="none" />
        <circle cx="48" cy="48" r="2" fill={ORANGE} />
        <rect x="22" y="68" width="52" height="38" rx="9" fill={TEAL_DEEP} />
        <rect x="24" y="70" width="48" height="34" rx="7" fill={TEAL} />
        <rect x="29" y="76" width="38" height="22" rx="5" fill={NEAR_BLACK} />
        <polygon points="34,83 43,83 38.5,94" fill={SIGNAL_BLUE} />
        <polygon points="53,83 62,83 57.5,94" fill={SIGNAL_BLUE} />
        <rect x="20" y="106" width="56" height="26" rx="7" fill={TEAL_DEEP} />
        <rect x="22" y="108" width="52" height="22" rx="5" fill={TEAL} />
        {/* Camera held up to face */}
        <rect x="68" y="84" width="22" height="14" rx="2" fill={NEAR_BLACK} />
        <circle cx="79" cy="91" r="4" fill={TEAL_DEEP} />
        <circle cx="79" cy="91" r="2" fill={ORANGE} />
        {/* Shutter flash burst */}
        <g className="nv-f-flash" opacity={0}>
          <circle cx="79" cy="91" r="14" fill={IVORY} opacity={0.6} />
          <line x1="79" y1="73" x2="79" y2="79" stroke={IVORY} strokeWidth="1.4" />
          <line x1="79" y1="103" x2="79" y2="109" stroke={IVORY} strokeWidth="1.4" />
          <line x1="61" y1="91" x2="67" y2="91" stroke={IVORY} strokeWidth="1.4" />
          <line x1="91" y1="91" x2="97" y2="91" stroke={IVORY} strokeWidth="1.4" />
        </g>
      </g>

      {/* Subject 1 — Mars red sibling */}
      <g className="nv-f-subject-1">
        <line x1="128" y1="80" x2="128" y2="66" stroke={NEAR_BLACK} strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="128" cy="62" r="2" fill={ORANGE} />
        <rect x="108" y="80" width="40" height="32" rx="8" fill={MARS_RED} />
        <rect x="113" y="86" width="30" height="20" rx="4" fill={NEAR_BLACK} />
        <polygon points="116,92 126,92 121,104" fill={SIGNAL_BLUE} />
        <polygon points="130,92 140,92 135,104" fill={SIGNAL_BLUE} />
        <rect x="108" y="112" width="40" height="22" rx="6" fill={MARS_RED} />
      </g>

      {/* Subject 2 — Andromeda blue sibling */}
      <g className="nv-f-subject-2">
        <line x1="170" y1="80" x2="170" y2="66" stroke={NEAR_BLACK} strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="170" cy="62" r="2" fill={ORANGE} />
        <rect x="152" y="82" width="36" height="30" rx="7" fill={ANDROMEDA} />
        <rect x="156" y="88" width="28" height="18" rx="4" fill={NEAR_BLACK} />
        <polygon points="159,93 169,93 164,104" fill={SIGNAL_BLUE} />
        <polygon points="171,93 181,93 176,104" fill={SIGNAL_BLUE} />
        <rect x="152" y="112" width="36" height="22" rx="6" fill={ANDROMEDA} />
      </g>
    </g>
  );
}

// ─── Animations ─────────────────────────────────────────────────
//
// CSS scoped via .nv-* classes. Each vignette runs its own narrative
// loop independently. Reduced-motion picks them up and slows everything
// way down so motion-sensitive users still see Nova "doing something"
// but with the energy taken out.

const vignetteCSS = `
  /* Workstation — float + tap + screen pulse + antenna orbit */
  @keyframes nv-w-float {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-3px); }
  }
  @keyframes nv-w-arm-tap-l {
    0%, 100% { transform: translateY(0); }
    25%      { transform: translateY(-2px); }
    50%      { transform: translateY(0); }
    75%      { transform: translateY(-1px); }
  }
  @keyframes nv-w-arm-tap-r {
    0%, 100% { transform: translateY(-1px); }
    25%      { transform: translateY(0); }
    50%      { transform: translateY(-2px); }
    75%      { transform: translateY(0); }
  }
  @keyframes nv-w-screen-pulse {
    0%, 100% { opacity: 0.55; }
    50%      { opacity: 1; }
  }
  @keyframes nv-w-orbit {
    0%   { transform: translateX(0) translateY(0); }
    25%  { transform: translateX(7px) translateY(-1px); }
    50%  { transform: translateX(0) translateY(0); }
    75%  { transform: translateX(-7px) translateY(1px); }
    100% { transform: translateX(0) translateY(0); }
  }
  .nv-workstation .nv-w-body {
    animation: nv-w-float 3s cubic-bezier(0.4,0,0.2,1) infinite;
    transform-origin: center bottom;
  }
  .nv-workstation .nv-w-arm-left {
    animation: nv-w-arm-tap-l 0.8s steps(2, end) infinite;
    transform-origin: 65px 138px;
  }
  .nv-workstation .nv-w-arm-right {
    animation: nv-w-arm-tap-r 0.8s steps(2, end) infinite;
    transform-origin: 135px 138px;
  }
  .nv-workstation .nv-w-screen-pulse {
    animation: nv-w-screen-pulse 1.8s ease-in-out infinite;
    transform-origin: center;
  }
  .nv-workstation .nv-w-orbit-dot {
    animation: nv-w-orbit 6s linear infinite;
    transform-origin: 100px 58px;
    transform-box: fill-box;
  }

  /* Garden — 12s narrative loop. Beats:
      0-3s    grow      (stem extends, bloom appears)
      3-5s    curious   (eyes tilt inward)
      5-7s    shocked   (eyes pop wide)
      7-8s    zap       (watering can swaps to blaster, beam visible)
      8-10s   smug      (smile-arc eyes, bloom withers)
     10-12s   reset
  */
  @keyframes nv-g-stem {
    0%   { transform: scaleY(0); }
    25%  { transform: scaleY(1); }
    66.7% { transform: scaleY(1); }   /* hold through curious + shock */
    66.71% { transform: scaleY(1); }
    83.3% { transform: scaleY(1); }   /* zap moment */
    83.31% { transform: scaleY(0); }  /* withered after the zap */
    100% { transform: scaleY(0); }
  }
  @keyframes nv-g-bloom {
    0%, 25%   { r: 0; }
    33%, 66.7% { r: 5; }
    83.3%     { r: 5; }
    83.31%    { r: 0; }
    100%      { r: 0; }
  }
  @keyframes nv-g-eyes-idle {
    0%, 25%, 91.7%, 100% { opacity: 1; }
    25.01%, 91.69%       { opacity: 0; }
  }
  @keyframes nv-g-eyes-curious {
    0%, 25%, 41.7%, 100% { opacity: 0; }
    25.01%, 41.69%       { opacity: 1; }
  }
  @keyframes nv-g-eyes-shocked {
    0%, 41.7%, 66.7%, 100% { opacity: 0; }
    41.71%, 66.69%         { opacity: 1; }
  }
  @keyframes nv-g-eyes-smug {
    0%, 66.7%, 91.7%, 100% { opacity: 0; }
    66.71%, 91.69%         { opacity: 1; }
  }
  @keyframes nv-g-watering {
    0%, 41.7%   { opacity: 1; transform: translateX(0); }
    44%, 91.7%  { opacity: 0; transform: translateX(-12px); }
    100%        { opacity: 1; transform: translateX(0); }
  }
  @keyframes nv-g-blaster {
    0%, 66.7%   { opacity: 0; transform: translateX(-12px); }
    69%, 83.3%  { opacity: 1; transform: translateX(0); }
    86%, 100%   { opacity: 0; transform: translateX(-12px); }
  }
  @keyframes nv-g-zap {
    0%, 75%        { opacity: 0; }
    75.01%, 83.3%  { opacity: 1; }
    83.31%, 100%   { opacity: 0; }
  }
  .nv-garden .nv-g-stem {
    animation: nv-g-stem 12s linear infinite;
    transform-origin: 149px 148px;
  }
  .nv-garden .nv-g-bloom { animation: nv-g-bloom 12s linear infinite; }
  .nv-garden .nv-g-eyes-idle    { animation: nv-g-eyes-idle    12s steps(1, end) infinite; }
  .nv-garden .nv-g-eyes-curious { animation: nv-g-eyes-curious 12s steps(1, end) infinite; }
  .nv-garden .nv-g-eyes-shocked { animation: nv-g-eyes-shocked 12s steps(1, end) infinite; }
  .nv-garden .nv-g-eyes-smug    { animation: nv-g-eyes-smug    12s steps(1, end) infinite; }
  .nv-garden .nv-g-watering { animation: nv-g-watering 12s ease-in-out infinite; }
  .nv-garden .nv-g-blaster  { animation: nv-g-blaster  12s ease-in-out infinite; }
  .nv-garden .nv-g-zap      { animation: nv-g-zap      12s steps(1, end) infinite; }

  /* Family portrait — 3s shutter cycle, subjects re-pose every 5s/6s. */
  @keyframes nv-f-flash {
    0%, 92%     { opacity: 0; }
    92.01%, 96% { opacity: 1; }
    96.01%, 100%{ opacity: 0; }
  }
  @keyframes nv-f-subject-1-pose {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-2px) rotate(-3deg); }
  }
  @keyframes nv-f-subject-2-pose {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-1px) rotate(3deg); }
  }
  .nv-family .nv-f-flash      { animation: nv-f-flash 3s ease-in-out infinite; }
  .nv-family .nv-f-subject-1  {
    animation: nv-f-subject-1-pose 5s cubic-bezier(0.4,0,0.2,1) infinite;
    transform-origin: 128px 134px;
  }
  .nv-family .nv-f-subject-2  {
    animation: nv-f-subject-2-pose 6s cubic-bezier(0.4,0,0.2,1) infinite;
    transform-origin: 170px 134px;
  }

  @media (prefers-reduced-motion: reduce) {
    .nv-workstation *, .nv-garden *, .nv-family * {
      animation-duration: 30s !important;
    }
  }
`;
